import {useEffect, useRef, useState} from 'react';
import styles from './FlowBuilder.module.scss';

const ANCHOR_OFFSET = 18; // pushes anchors past column padding into the gap

/**
 * Draws n8n-style smooth-curved connector lines between:
 *   - the selected category row → every feature row
 *   - the selected feature row → every issue row
 *
 * The component is an absolute-positioned SVG overlay covering the columns
 * grid. It recomputes paths on selection change, on window resize, and on
 * scroll inside the column bodies, using `data-flow-row` IDs on each row.
 */
const DESKTOP_BREAKPOINT = '(min-width: 1081px)';

export const FlowConnectors = ({
  hostRef,
  selectedCategoryId,
  selectedFeatureId,
  features,
  issues,
}) => {
  const [paths, setPaths] = useState([]);
  const [box, setBox] = useState({width: 0, height: 0});
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window === 'undefined' ||
      window.matchMedia(DESKTOP_BREAKPOINT).matches,
  );
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_BREAKPOINT);
    const onChange = e => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !isDesktop) return undefined;

    const measure = () => {
      const hostRect = host.getBoundingClientRect();
      setBox({width: hostRect.width, height: hostRect.height});

      const anchorOf = (id, side) => {
        const el = host.querySelector(`[data-flow-row="${id}"]`);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const y = r.top + r.height / 2 - hostRect.top;
        const x =
          side === 'right'
            ? r.right - hostRect.left + ANCHOR_OFFSET
            : r.left - hostRect.left - ANCHOR_OFFSET;
        return {x, y};
      };

      const pairs = [];

      // Only draw the line from the selected category to the *selected*
      // feature (not all features) — keeps the active path obvious.
      if (selectedCategoryId && selectedFeatureId) {
        const from = anchorOf(`cat-${selectedCategoryId}`, 'right');
        const to = anchorOf(`feat-${selectedFeatureId}`, 'left');
        if (from && to) {
          pairs.push({
            id: `cat-${selectedCategoryId}-feat-${selectedFeatureId}`,
            from,
            to,
            active: true,
          });
        }
      }

      if (selectedFeatureId) {
        const from = anchorOf(`feat-${selectedFeatureId}`, 'right');
        if (from) {
          issues.forEach(issue => {
            const to = anchorOf(`issue-${issue.id}`, 'left');
            if (to) {
              pairs.push({
                id: `feat-${selectedFeatureId}-issue-${issue.id}`,
                from,
                to,
                active: false,
              });
            }
          });
        }
      }

      setPaths(pairs);
    };

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };

    schedule();

    const ro = new ResizeObserver(schedule);
    ro.observe(host);

    // Watch column bodies for scroll so curves stay anchored to rows.
    const bodies = host.querySelectorAll('[data-flow-scroll]');
    bodies.forEach(b =>
      b.addEventListener('scroll', schedule, {passive: true}),
    );
    window.addEventListener('resize', schedule);

    return () => {
      ro.disconnect();
      bodies.forEach(b => b.removeEventListener('scroll', schedule));
      window.removeEventListener('resize', schedule);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    hostRef,
    selectedCategoryId,
    selectedFeatureId,
    features,
    issues,
    isDesktop,
  ]);

  if (!isDesktop || !paths.length || !box.width) return null;

  return (
    <svg
      className={styles.flowConnectors}
      width={box.width}
      height={box.height}
      viewBox={`0 0 ${box.width} ${box.height}`}>
      <defs>
        <linearGradient id="flow-line" x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="flow-line-active" x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="1" />
          <stop offset="100%" stopColor="#4f46e5" stopOpacity="1" />
        </linearGradient>
      </defs>
      {paths.map(({id, from, to, active}) => {
        const dx = Math.max(40, (to.x - from.x) / 2);
        const d = `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${
          to.x - dx
        } ${to.y}, ${to.x} ${to.y}`;
        return (
          <g key={id}>
            <path
              d={d}
              fill="none"
              stroke={active ? 'url(#flow-line-active)' : 'url(#flow-line)'}
              strokeWidth={active ? 2.5 : 1.75}
              strokeLinecap="round"
            />
            <circle
              cx={from.x}
              cy={from.y}
              r={active ? 3.5 : 3}
              fill={active ? '#4f46e5' : '#6366f1'}
            />
            <circle
              cx={to.x}
              cy={to.y}
              r={active ? 3.5 : 3}
              fill={active ? '#4f46e5' : '#6366f1'}
            />
          </g>
        );
      })}
    </svg>
  );
};
